import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { FaSearch, FaUser, FaEnvelope } from 'react-icons/fa';
import { searchUser } from '../../store/authSlice';

const SearchContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const SearchButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const UserCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const UserIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: white;
  font-size: 1.2rem;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.2rem;
`;

const UserEmail = styled.p`
  margin: 0.25rem 0 0 0;
  color: #666;
  font-size: 0.9rem;
`;

const UserDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  font-size: 0.9rem;
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #fcc;
  margin-bottom: 1rem;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  color: #666;
  padding: 2rem;
  font-style: italic;
`;

const UserSearchComponent = () => {
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((state) => state.auth);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchTerm.trim()) {
      return;
    }

    setHasSearched(true);
    setSearchResults(null);

    try {
      const result = await dispatch(searchUser(searchTerm.trim())).unwrap();
      setSearchResults(result.user);
    } catch (error) {
      setSearchResults(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const formatStorageLimit = (seconds) => {
    if (!seconds) return 'No limit';
    const hours = Math.floor(seconds / 3600);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days} days`;
    return `${hours} hours`;
  };

  return (
    <SearchContainer>
      <h1 style={{ textAlign: 'center', marginBottom: '2rem', color: '#333' }}>
        Search Users
      </h1>

      <SearchForm onSubmit={handleSearch}>
        <SearchInput
          type="text"
          placeholder="Search by username or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <SearchButton type="submit" disabled={isLoading || !searchTerm.trim()}>
          {isLoading ? <LoadingSpinner /> : <FaSearch />}
        </SearchButton>
      </SearchForm>

      {error && hasSearched && (
        <ErrorMessage>
          {error.includes('404') || error.includes('not found') 
            ? 'User not found. Please check the username or email and try again.'
            : error.includes('401') || error.includes('unauthorized')
            ? 'Please log in to search for users.'
            : error
          }
        </ErrorMessage>
      )}

      {hasSearched && !isLoading && !searchResults && !error && (
        <NoResultsMessage>
          No user found with that username or email.
        </NoResultsMessage>
      )}

      {searchResults && (
        <UserCard>
          <UserHeader>
            <UserIcon>
              <FaUser />
            </UserIcon>
            <UserInfo>
              <UserName>{searchResults.username}</UserName>
              <UserEmail>{searchResults.email}</UserEmail>
            </UserInfo>
          </UserHeader>

          <UserDetails>
            <DetailItem>
              <FaEnvelope />
              <span>Email: {searchResults.email}</span>
            </DetailItem>
            <DetailItem>
              <FaUser />
              <span>Username: {searchResults.username}</span>
            </DetailItem>
            <DetailItem>
              <span>Status: {searchResults.enabled ? 'Active' : 'Inactive'}</span>
            </DetailItem>
            <DetailItem>
              <span>Date of Birth: {formatDate(searchResults.dob)}</span>
            </DetailItem>
            <DetailItem>
              <span>TikTok Connected: {searchResults.tikTokConnected ? 'Yes' : 'No'}</span>
            </DetailItem>
            <DetailItem>
              <span>Storage Limit: {formatStorageLimit(searchResults.storageLimitInSeconds)}</span>
            </DetailItem>
            {searchResults.roles && searchResults.roles.length > 0 && (
              <DetailItem>
                <span>Roles: {searchResults.roles.join(', ')}</span>
              </DetailItem>
            )}
            {searchResults.createdAt && (
              <DetailItem>
                <span>Member Since: {formatDate(searchResults.createdAt)}</span>
              </DetailItem>
            )}
          </UserDetails>
        </UserCard>
      )}
    </SearchContainer>
  );
};

export default UserSearchComponent;
